import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Save, 
  CheckCircle, 
  AlertTriangle, 
  Zap,
  Truck,
  Users,
  Trash2,
  Droplets,
  FileCheck,
  Download,
  Upload,
  Plus,
  Lock
} from 'lucide-react';

const DataCollectionWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  // State management
  const [selectedCategory, setSelectedCategory] = useState('Energy');
  const [selectedMethod, setSelectedMethod] = useState('ERP');
  const [selectedActivities, setSelectedActivities] = useState({
    energy_consumption: false,
    fuel_consumption: false,
    distance_activity: false,
    tonne_km_activity: false,
    hr_aggregate: false,
    waste_mass: false,
    water_volume: false,
    policy_reference: false
  });
  const [coverage] = useState({
    Energy: 0,
    Transport: 0,
    HR: 0,
    Waste: 0,
    Water: 0,
    Policies: 0
  });
  const [assumptions, setAssumptions] = useState([
    { name: t('workflow.dataCollection.assumptionItems.default_emission_factors'), status: t('workflow.dataCollection.statusOptions.draft'), owner: t('workflow.dataCollection.owners.esg_team') },
    { name: t('workflow.dataCollection.assumptionItems.activity_data_assumptions'), status: t('workflow.dataCollection.statusOptions.approved'), owner: t('workflow.dataCollection.owners.operations') }
  ]);
  const [dataFreeze, setDataFreeze] = useState(null);
  const [errors] = useState(0);
  const [approvedAssumptions] = useState(1);

  const categories = ['Energy', 'Transport', 'HR', 'Waste', 'Water', 'Policies'];
  
  const categoryTranslations = {
    'Energy': t('workflow.dataCollection.categories.energy'),
    'Transport': t('workflow.dataCollection.categories.transport'),
    'HR': t('workflow.dataCollection.categories.hr'),
    'Waste': t('workflow.dataCollection.categories.waste'),
    'Water': t('workflow.dataCollection.categories.water'),
    'Policies': t('workflow.dataCollection.categories.policies')
  };

  const activityLabels = {
    energy_consumption: t('workflow.dataCollection.activities.energy_consumption'),
    fuel_consumption: t('workflow.dataCollection.activities.fuel_consumption'),
    distance_activity: t('workflow.dataCollection.activities.distance_activity'),
    tonne_km_activity: t('workflow.dataCollection.activities.tonne_km_activity'),
    hr_aggregate: t('workflow.dataCollection.activities.hr_aggregate'),
    waste_mass: t('workflow.dataCollection.activities.waste_mass'),
    water_volume: t('workflow.dataCollection.activities.water_volume'),
    policy_reference: t('workflow.dataCollection.activities.policy_reference')
  };


  const handleActivityToggle = (activity) => {
    setSelectedActivities(prev => ({
      ...prev,
      [activity]: !prev[activity]
    }));
  };


  const handleAddAssumption = () => {
    setAssumptions(prev => [...prev, {
      name: t('workflow.dataCollection.assumptionItems.new_assumption'),
      status: t('workflow.dataCollection.statusOptions.draft'),
      owner: t('workflow.dataCollection.owners.user')
    }]);
  };

  const handleFreezeData = () => {
    setDataFreeze(new Date().toISOString());
  };

  const isDefinitionOfDone = () => {
    return errors === 0 && 
           Object.values(coverage).some(c => c >= 80) && 
           approvedAssumptions > 0 && 
           dataFreeze !== null;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Energy': return <Zap className="w-4 h-4" />;
      case 'Transport': return <Truck className="w-4 h-4" />;
      case 'HR': return <Users className="w-4 h-4" />;
      case 'Waste': return <Trash2 className="w-4 h-4" />;
      case 'Water': return <Droplets className="w-4 h-4" />;
      case 'Policies': return <FileCheck className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 relative">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('workflow.dataCollection.title')}</h1>
        </div>
      </div>

      {/* Header Chips */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <AlertTriangle className="w-4 h-4" />
          {t('workflow.dataCollection.errors')}: {errors}
        </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          <CheckCircle className="w-4 h-4" />
          {t('workflow.dataCollection.coverage')}: {Math.max(...Object.values(coverage))}%
              </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-4 h-4" />
          {t('workflow.dataCollection.assumptions')}: {approvedAssumptions} {t('workflow.dataCollection.statusOptions.approved')}
            </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
          <Lock className="w-4 h-4" />
          {t('workflow.dataCollection.freeze')}: {dataFreeze ? new Date(dataFreeze).toLocaleDateString() : '—'}
        </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* 1) Category */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">1) {t('workflow.dataCollection.category')}</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getCategoryIcon(category)}
                  {categoryTranslations[category]}
                </button>
              ))}
            </div>
            </div>

          {/* 2) Method */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">2) {t('workflow.dataCollection.method')} (exclusive)</h3>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSelectedMethod('ERP')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  selectedMethod === 'ERP'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('workflow.dataCollection.erp')}
              </button>
              <button
                onClick={() => setSelectedMethod('CSV')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  selectedMethod === 'CSV'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('workflow.dataCollection.csv')}
              </button>
        </div>
      </div>

          {/* 3A) ERP */}
          {selectedMethod === 'ERP' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3A) {t('workflow.dataCollection.erp')}</h3>
              
              {/* Emission Activities */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('workflow.dataCollection.emissionActivities')} (checkboxes)</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(activityLabels).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedActivities[key]}
                        onChange={() => handleActivityToggle(key)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
        </div>
      </div>


              {/* Coverage */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('workflow.dataCollection.coverageReadOnly')}</h4>
                <div className="space-y-2">
                  {Object.entries(coverage).map(([category, value]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{category}</span>
                      <span className="text-sm font-medium text-gray-900">{value}%</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* 3B) CSV */}
          {selectedMethod === 'CSV' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3B) {t('workflow.dataCollection.csv')}</h3>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                    <Download className="w-4 h-4" />
                    {t('workflow.dataCollection.downloadTemplate')}
                </button>
                  <button className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
                    <Upload className="w-4 h-4" />
                    {t('workflow.dataCollection.uploadFile')}
                </button>
              </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">{t('workflow.dataCollection.acceptedErrors')}</div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">(Optional) {t('workflow.dataCollection.schedule')}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('workflow.dataCollection.frequency')}</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option>{t('workflow.dataCollection.frequencyOptions.daily')}</option>
                        <option>{t('workflow.dataCollection.frequencyOptions.weekly')}</option>
                        <option>{t('workflow.dataCollection.frequencyOptions.monthly')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('workflow.dataCollection.deadlineDay')}</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="15"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{t('workflow.dataCollection.coverageReadOnly')}</h4>
                  <div className="space-y-2">
                    {Object.entries(coverage).map(([category, value]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{category}</span>
                        <span className="text-sm font-medium text-gray-900">{value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* 4) Assumptions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">4) {t('workflow.dataCollection.assumptionsCompact')}</h3>
              <button
                onClick={handleAddAssumption}
                className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                {t('workflow.dataCollection.add')}
              </button>
        </div>

        <div className="space-y-3">
              <div className="grid grid-cols-4 gap-4 text-xs font-medium text-gray-600 border-b border-gray-200 pb-2">
                <div>{t('workflow.dataCollection.name')}</div>
                <div>{t('workflow.dataCollection.status')}</div>
                <div>{t('workflow.dataCollection.owner')}</div>
                <div></div>
              </div>
              {assumptions.map((assumption, index) => (
                <div key={index} className="grid grid-cols-4 gap-4 items-center text-sm">
                  <div className="text-gray-900">{assumption.name}</div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      assumption.status === t('workflow.dataCollection.statusOptions.approved') 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {assumption.status}
                </span>
              </div>
                  <div className="text-gray-600">{assumption.owner}</div>
                  <div></div>
            </div>
          ))}
            </div>
        </div>

          {/* 5) Freeze */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">5) {t('workflow.dataCollection.freeze')}</h3>
            <div className="space-y-4">
              <button
                onClick={handleFreezeData}
                disabled={!isDefinitionOfDone()}
                className="flex items-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Lock className="w-4 h-4" />
                {t('workflow.dataCollection.freezeData')}
              </button>
              
              {dataFreeze && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">{t('workflow.dataCollection.snapshotTimestamp')}:</div>
                  <div className="text-sm font-medium text-gray-900">
                    {new Date(dataFreeze).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Definition of Done */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workflow.dataCollection.definitionOfDone')}</h3>
            <div className="space-y-2">
              <div className={`flex items-center gap-2 text-sm ${
                errors === 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                <CheckCircle className="w-4 h-4" />
                {t('workflow.dataCollection.errorsZero')}
              </div>
              <div className={`flex items-center gap-2 text-sm ${
                Object.values(coverage).some(c => c >= 80) ? 'text-green-600' : 'text-gray-600'
              }`}>
                <CheckCircle className="w-4 h-4" />
                {t('workflow.dataCollection.coverageThreshold')}
            </div>
              <div className={`flex items-center gap-2 text-sm ${
                approvedAssumptions > 0 ? 'text-green-600' : 'text-gray-600'
              }`}>
                <CheckCircle className="w-4 h-4" />
                {t('workflow.dataCollection.assumptionsApproved')}
            </div>
              <div className={`flex items-center gap-2 text-sm ${
                dataFreeze !== null ? 'text-green-600' : 'text-gray-600'
              }`}>
                <CheckCircle className="w-4 h-4" />
                {t('workflow.dataCollection.dataFreezeCreated')}
            </div>
            </div>
          </div>
        </div>
       </div>
       
      {/* Action Buttons - Bottom */}
      <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
         <button 
          onClick={() => console.log('Save')}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
         >
          <Save className="w-4 h-4" />
          {t('workflow.dataCollection.save')}
         </button>
      </div>
      {previewOnly && (
        <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] z-10 rounded-lg">
        </div>
      )}
    </div>
   );
 };

export default DataCollectionWorkflow;